import { BrowserRouter as Router, Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useEffect, useLayoutEffect, useState, lazy, Suspense } from "react";
import "./App.css";

// Toast notifications
import { ToastProvider } from "./components/Toast/ToastContext";
import { ConfirmProvider } from "./components/ConfirmModal/ConfirmContext";
import UpdatePrompt from "./components/UpdatePrompt";
import RootErrorBoundary from "./components/RootErrorBoundary";
import RecentRoutes from "./components/RecentRoutes";

// Zustand stores
import useUserStore from "./stores/useUserStore";
import useFleetStore from "./stores/useFleetStore";
import usePartsStore from "./stores/usePartsStore";
import useNavPrefsStore from "./stores/useNavPrefsStore";
import { useThemeStore } from "./stores/useThemeStore";

// AI Chatbot
import FlerpieChatBot from "./components/ChatBot/FlerpieChatBot";
import { DOCK_MOTION } from "./components/ChatBot/flerpieDock";
import { isSwarm } from "./instance";
import { routeAllowedOnInstance } from "./navigation/routeInstances";
import { userCanAccess } from './navigation/menuConfig';

/* BASE UI */
import NavBar from "./NavBar/NavBar";
import FleetDashboard from "./Dashboard/FleetDashboard";
import { useDeviceMode } from "./Scanner/useDeviceMode";
import Login from "./Login/Login";
import RegisterUser from "./Login/RegisterUser"
import ResetPass from "./Login/ResetPass"
import PilotAuth from "./Login/PilotAuth";
import OAuthConsent from "./Login/OAuthConsent";
import LogoSpinner from "./components/ui/LogoSpinner";

/* DRONES */
import DroneFleet from "./DroneFleet/DroneFleet";
import FleetReconcilePage from "./DroneFleet/ReconcilePage";
import Drone from "./DroneFleet/DronePage/DronePage";
import BoardPage from "./DroneFleet/BoardPage/BoardPage";
import PartInstancePage from "./Parts/PartInstancePage";
import PartLookupPage from "./Parts/PartLookupPage";
import AddBatch from "./DroneFleet/AddBatch/AddBatch";
import DroneMap from "./DroneMap/DroneMap";

/* MAINTENANCE */
import VehicleHistory from "./DroneFleet/VehicleHistory/VehicleHistory";
import EditHistoryPopup from "./DroneFleet/VehicleHistory/EditHistoryPopup";
import MaintLogPage from "./MaintLogPage/MaintLogPage";

/* INVENTORY */
// Legacy Parts page + AddPart removed in Phase 1b.08 cutover. Replacements:
// PartsCatalogPage, PartMPNDetail (TSX), InventoryPage. New SKUs are
// auto-created by procurement-order placement, not by a user-facing form.
// Legacy PartDetails + InvLogPage removed in Phase 1b.08 cutover. Replacements:
// PartMPNDetail (catalog row drilldown) + ReceiptItemDetail (per-item history).
// inventory_transactions is the new audit-trail table; the legacy
// inventorylogs CRUD page has no equivalent (transactions are surfaced
// in-context on the receipt-item detail view).

/* CONFIGURATIONS */
import Configurations from "./Configurations/ConfigurationsList";
import ConfigurEdit from "./Configurations/ConfigurEdit/ConfigurEdit";

/* MISC & INCOMPLETE */
import GoldStars from "./Stars/GoldStars";
import Leaderboard from "./Stars/Leaderboard";
import Timeline from "./Stars/Timeline";
import SerialNumberGenerator from "./CSVFunctions/SerialNumberGenerator";
import SerialMint from "./DroneFleet/SerialMint/SerialMint";
import SettingsPage from "./Settings/SettingsPage";
import ChargeControl from "./ChargeControl/ChargeControl";
import AdminPage from "./HomeScreen/AdminPage";
import GhostFlagsAdmin from "./HomeScreen/GhostFlagsAdmin";
import FAARegistrations from "./CSVFunctions/FAARegistrations";
import ShopScreen from "./HomeScreen/ShopScreen/ShopScreen";
import FishScreen from "./HomeScreen/ShopScreen/FishScreen";
import OfficeScreen from "./OfficeScreen/OfficeScreen";
import ShopFloor from "./ShopFloor/ShopFloor";
import BatchPrint from "./BatchPrint/BatchPrint";
import LabelMaker from "./BatchPrint/LabelMaker";

/* ANALYSIS */
import AnalysisPage from "./Analysis/AnalysisPage";

/* RMA */
import RMASubmit from "./RMAPublic/RMASubmit";
import RMAStatus from "./RMAPublic/RMAStatus";
import RMAList from "./RMA/RMAList";
import RMADetails from "./RMA/RMADetails";
import RMACatalogAdmin from "./RMA/RMACatalogAdmin";

/* FIRMWARE HUB */
import FirmwarePage from "./Firmware/FirmwarePage";
import FirmwareDetail from "./Firmware/FirmwareDetail";

/* ADMIN */
import UserAdmin from "./Admin/UserAdmin";
import APIKeys from "./Admin/APIKeys";
import ScannerDevices from "./Admin/ScannerDevices";
import FlashOperations from "./Admin/FlashOperations";
import AIControls from "./Admin/AIControls";
import ManufacturingReport from "./Admin/ManufacturingReport";
import FlerpieDebugDashboard from "./components/ChatBot/FlerpieDebugDashboard";

/* SCANNER */
import ScannerMode from "./Scanner/ScannerMode";

/* STATISTICS */
import Statistics from "./Statistics/Statistics";

/* ERP - Events, Sales */
import EventList from "./Events/EventList";
import EventForm from "./Events/EventForm";
import EventDetail from "./Events/EventDetail";
import EventCalendar from "./Events/EventCalendar";
import MondayInbox from "./Events/MondayInbox";
import DispatchPage from "./Events/DispatchPage";
import PilotsPage from "./Pilots/PilotsPage";
import POAPage from "./POA/POAPage";
import PackingPage from "./Packing/PackingPage";
import VehiclesPage from "./Vehicles/VehiclesPage";
import SchedulePage from "./VehicleSchedule/SchedulePage";
import TourList from "./Tours/TourList";
import TourDetail from "./Tours/TourDetail";
import OrdersPage from "./Sales/OrdersPage";
import SalesOrderForm from "./Sales/SalesOrderForm";
import SalesOrderDetail from "./Sales/SalesOrderDetail";
import FulfillmentPage from "./Sales/FulfillmentPage";
import CustomerList from "./Customers/CustomerList";
import CustomerDetail from "./Customers/CustomerDetail";
import CustomerTransfers from "./Customers/CustomerTransfers";
import CustomerForm from "./Customers/CustomerForm";
import AssetsPage from "./Assets/AssetsPage";
import AssetsTypeList from "./Assets/AssetsTypeList";
import AssetsStableList from "./Assets/AssetsStableList";
import AssetDetail from "./Assets/AssetDetail";
import NewAssetForm from "./Assets/NewAssetForm";
import AssetTypesAdmin from "./Admin/AssetTypesAdmin";
import VendorTypesAdmin from "./Admin/VendorTypesAdmin";
import ItemKindsAdmin from "./Admin/ItemKindsAdmin";
import StaffSchedulePage from "./Schedule/StaffSchedulePage";
import MySchedulePage from "./Schedule/MySchedulePage";
import RosterPage from "./Schedule/RosterPage";

/* ERP - Receiving (Phase 1b) */
import ReceivingHubPage from "./Receiving/ReceivingHubPage";
import ReceiptItemDetail from "./Receiving/ReceiptItemDetail";
import ReceiptBoxPage from "./Receiving/ReceiptBoxPage";
import ReceiveOrderPage from "./Receiving/ReceiveOrderPage";
import PackingListPage from "./Receiving/PackingListPage";
import CatalogImportPage from "./Parts/CatalogImportPage";
import ShipmentsPage from "./Receiving/ShipmentsPage";
import ShipmentDetail from "./Receiving/ShipmentDetail";
import PartsCatalogPage from "./Parts/PartsCatalogPage";
import PartMPNDetail from "./Parts/PartMPNDetail";
import InventoryPage from "./Parts/InventoryPage";
import ProductBomsPage from "./Parts/ProductBomsPage";
import BomPage from "./Parts/BomPage";
import PullsReportPage from "./Parts/Pulls/PullsReportPage";
import ScrapBinPage from "./Parts/Pulls/ScrapBinPage";
import WorkOrdersPage from "./WorkOrders/WorkOrdersPage";
import WorkOrderPage from "./WorkOrders/WorkOrderPage";
import AssemblyPickPage from "./WorkOrders/AssemblyPickPage";
import ReceiptsPage from "./Receipts/ReceiptsPage";
import ReceiptReviewPage from "./Receipts/ReceiptReviewPage";
import RequisitionsPage from "./Requisitions/RequisitionsPage";
import NewRequisitionPage from "./Requisitions/NewRequisitionPage";
import RequisitionDetailPage from "./Requisitions/RequisitionDetailPage";
import ReconcilePage, { ReconcileBoard } from "./Receipts/ReconcilePage";
import InvoiceQueuePage from "./AccountsPayable/InvoiceQueuePage";
import InvoiceDetailPage from "./AccountsPayable/InvoiceDetailPage";
import LedgerPage from "./Receipts/LedgerPage";
import InsightsPage from "./Receipts/InsightsPage";
import NodePlansPage from "./NodePlans/NodePlansPage";
import NodePlanPage from "./NodePlans/NodePlanPage";
import SparkPage from "./Spark/SparkPage";
import SparkProjectPage from "./Spark/SparkProjectPage";
import SparkMyWork from "./Spark/SparkMyWork";
import SparkStudio from "./Spark/SparkStudio";
import AsanaImport from "./Admin/AsanaImport";
import FinancialLogsPage from "./Parts/FinancialLogsPage";
import PartFamiliesAdmin from "./Admin/PartFamiliesAdmin";
import PartFamilyPage from "./Admin/PartFamilyPage";
import { canEditFamilies } from "./Parts/familyAccess";
import LocationsAdmin from "./Admin/LocationsAdmin";
import CardManager from "./Admin/CardManager";
import VendorsAdmin from "./Admin/VendorsAdmin";
import PurchaseOrderList from "./Procurement/PurchaseOrderList";
import PurchaseOrderForm from "./Procurement/PurchaseOrderForm";
import PurchaseOrderDetail from "./Procurement/PurchaseOrderDetail";
import PoImportReview from "./Procurement/PoImportReview";
import PurchaseReturnsPage from "./Procurement/PurchaseReturnsPage";
import BoardReturnsPage from "./Procurement/BoardReturns/BoardReturnsPage";
import BoardReturnManifest from "./Procurement/BoardReturns/BoardReturnManifest";
import PoScanPage from "./Procurement/PoScanPage";
import QuoteDraftPage from "./Procurement/QuoteDraftPage";
import MatchBoardPage from "./Procurement/MatchBoardPage";
import ApprovalSettings from "./Admin/ApprovalSettings";

// Lazily loaded on purpose: a hidden game should not sit in everyone's main bundle.
// Reached by tapping the Settings header five times, or by typing the URL.
//
// Must stay BELOW every import: eslint's import/first counts a const between
// imports as module body, so a lazy() declaration up there marks all following
// imports as errors. CRA builds with CI=true, where that fails the build.
const PilotSimulator = lazy(() => import("./fun/PilotSimulator/PilotSimulator"));
// The PDF tool pulls in jspdf, so it loads only when someone opens an answer in it.
const FlerpiePdfPage = lazy(() => import("./components/ChatBot/FlerpiePdfPage"));
const FlerpieTimesPage = lazy(() => import("./components/ChatBot/FlerpieTimesPage"));

const App = () => {
  // Zustand store selectors
  const user = useUserStore((state) => state.user);
  const isInitializing = useUserStore((state) => state.isInitializing);
  const logout = useUserStore((state) => state.logout);
  const initializeAuth = useUserStore((state) => state.initializeAuth);

  const fleetLoading = useFleetStore((state) => state.loading);
  const fetchFleet = useFleetStore((state) => state.fetchFleet);

  // Shared-scanner kiosks land directly on the scanner; the dashboard would just be
  // noise on a shop-floor device. Settings, Logout, and any explicit URL still work.
  const [deviceMode] = useDeviceMode();
  const refreshFleet = useFleetStore((state) => state.refreshFleet);
  const clearFleet = useFleetStore((state) => state.clearFleet);

  const fetchParts = usePartsStore((state) => state.fetchParts);
  const refreshParts = usePartsStore((state) => state.refreshParts);
  const clearParts = usePartsStore((state) => state.clearParts);

  // Fetch all data (called after successful auth)
  const fetchAllData = () => {
    fetchFleet(handleLogout);
    fetchParts(handleLogout);
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    clearFleet();
    clearParts();
  };

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth(fetchAllData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh data when user returns to the tab (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only refresh if tab becomes visible and user is logged in
      if (!document.hidden && user.loggedin) {
        refreshFleet();
        refreshParts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user.loggedin, refreshFleet, refreshParts]);

  const renderRoutes = () => {
    // Show loading while checking initial auth state (prevents login flash)
    if (isInitializing || fleetLoading) return <Route path="*" component={LogoSpinner} />;
    if (!user.loggedin) {
      return (
        <Switch>
          <Route path="/register" component={RegisterUser} />
          <Route path="/resetpass" component={ResetPass} />
          {/* RMA routes now require login - removed from public access */}
          <Route path="*" component={Login} />
        </Switch>
      );
    }
    return (
      // Wraps rather than reindents the Switch below: the gate needs to resolve
      // the URL BEFORE a match is made, so a blocked page never mounts.
      <InstanceGate>
      <Switch>
        <Route exact path="/" render={() => deviceMode === 'shared' ? <Redirect to="/scanner" /> : <FleetDashboard />} />
        <Route exact path="/settings" component={SettingsPage} />
        {/* Easter egg. Absent from navTree so it never shows in nav or the palette;
            gated to Firefly in navigation/routeInstances.ts. */}
        <Route
          exact
          path="/pilot-simulator"
          render={() => (
            <Suspense fallback={<LogoSpinner />}>
              <PilotSimulator />
            </Suspense>
          )}
        />
        <Route path="/charge" render={() => (user.isadmin || user.can_admin || user.can_pilot || user.can_operations || user.can_manager || user.can_manufacturing) ? <ChargeControl /> : <Redirect to="/" />} />
        <Route exact path="/admin" render={() => user.isadmin ? <AdminPage /> : <Redirect to="/" />} />
        <Route exact path="/admin/ghost-flags" render={() => user.isadmin ? <GhostFlagsAdmin /> : <Redirect to="/" />} />
        <Route exact path="/admin/users" render={() => user.isadmin ? <UserAdmin /> : <Redirect to="/" />} />
        <Route exact path="/admin/apikeys" render={() => user.isadmin ? <APIKeys /> : <Redirect to="/" />} />
        <Route exact path="/admin/scanners" render={() => user.isadmin ? <ScannerDevices /> : <Redirect to="/" />} />
        <Route exact path="/admin/flash-operations" render={() => user.isadmin ? <FlashOperations /> : <Redirect to="/" />} />
        <Route exact path="/admin/manufacturing-report" render={() => user.isadmin ? <ManufacturingReport /> : <Redirect to="/" />} />
        <Route exact path="/admin/ai" render={() => user.isadmin ? <AIControls /> : <Redirect to="/" />} />
        <Route exact path="/admin/flerpie-debug" render={() => user.isadmin ? <FlerpieDebugDashboard /> : <Redirect to="/" />} />
        <Route
          exact
          path="/flerpie/pdf/:messageId"
          render={() => (user.can_ai || user.isadmin || user.can_admin) ? (
            <Suspense fallback={<LogoSpinner />}>
              <FlerpiePdfPage />
            </Suspense>
          ) : <Redirect to="/" />}
        />
        <Route
          exact
          path="/flerpie/times"
          render={() => (user.can_ai || user.isadmin || user.can_admin) ? (
            <Suspense fallback={<LogoSpinner />}>
              <FlerpieTimesPage />
            </Suspense>
          ) : <Redirect to="/" />}
        />
        <Route
          exact
          path="/flerpie/times/:id"
          render={() => (user.can_ai || user.isadmin || user.can_admin) ? (
            <Suspense fallback={<LogoSpinner />}>
              <FlerpieTimesPage />
            </Suspense>
          ) : <Redirect to="/" />}
        />
        <Route exact path="/shopscreen" component={ShopScreen} />
        <Route exact path="/shopscreen2" component={FishScreen} />
        <Route exact path="/shop" component={ShopFloor} />
        <Route exact path="/office" component={OfficeScreen} />
        <Route exact path="/loading" component={LogoSpinner} />

        <Route exact path="/fleet" component={DroneFleet} />
        <Route exact path="/admin/reconcile" render={() => (user.isadmin || user.can_admin) ? <FleetReconcilePage /> : <Redirect to="/" />} />
        <Route exact path="/firmware" render={() => (user.isadmin || user.can_admin || user.can_pilot || user.can_operations || user.can_manufacturing) ? <FirmwarePage /> : <Redirect to="/" />} />
        <Route exact path="/firmware/:hash" render={() => (user.isadmin || user.can_admin || user.can_pilot || user.can_operations || user.can_manufacturing) ? <FirmwareDetail /> : <Redirect to="/" />} />
        {/* Namespaced build page (e.g. /firmware/dronevis/:hash) — the slug scopes the resolver to one category */}
        <Route exact path="/firmware/:slug/:hash" render={() => (user.isadmin || user.can_admin || user.can_pilot || user.can_operations || user.can_manufacturing) ? <FirmwareDetail /> : <Redirect to="/" />} />
        <Route exact path="/fleet/firmware-labels" render={() => <Redirect to="/firmware" />} />
        <Route exact path="/map" component={DroneMap} />

        <Route exact path="/drone/:serialnumber" component={Drone} />
        <Route exact path="/add/multiple" component={AddBatch} />
        <Route exact path="/drone/history/:serialnumber" component={VehicleHistory} />
        <Route exact path="/drone/:serialnumber/:status/:id" component={EditHistoryPopup} />
        {/* Where a board sticker lands when it is not installed in a drone. */}
        <Route exact path="/board/:uid" component={BoardPage} />
        {/* A GPS, LED or pyro unit by its sticker, and the page that scans any sticker. */}
        <Route exact path="/part/:id" component={PartInstancePage} />
        <Route exact path="/parts/lookup" component={PartLookupPage} />

        <Route exact path="/configurations" component={Configurations} />
        <Route exact path="/configurations/edit" component={ConfigurEdit} />

        {/* Legacy /parts, /parts/:ffpn, /add/part, /inventorylogs routes
            removed in Phase 1b.08 cutover. Use /parts-catalog, /inventory,
            /receiving, /receiving/box/:id, /receiving/item/:id. */}

        {/* ERP - Receiving (Phase 1b). Operations | manufacturing | procurement | admin.
            /receiving/box/:id is the sticker's box, /receiving/item/:id one item
            in it; all three share the same capability gate. */}
        <Route exact path="/receiving" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ReceivingHubPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/receiving/box/:id" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ReceiptBoxPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/receiving/item/:id" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ReceiptItemDetail />
            : <Redirect to="/" />
        )} />
        {/* Receive a whole order in one press; a headerless pre-009 line gets the
            same page with one line. Same gate as the hub. */}
        <Route exact path="/receiving/order/:poId" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ReceiveOrderPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/receiving/line/:lineId" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ReceiveOrderPage />
            : <Redirect to="/" />
        )} />
        {/* ERP - Packing list reader (038). The SAME gate as the hub, deliberately:
            a slip is read at the boxes by whoever unloaded the truck, and it
            receives nothing. The backend route is under /procurement/ because
            that is where the AI document lanes are mounted, which is a backend
            arrangement rather than a statement about whose screen this is. */}
        <Route exact path="/receiving/packing-list" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <PackingListPage />
            : <Redirect to="/" />
        )} />
        {/* The shipment RECORD, read long after the delivery. /shipments rather
            than /packing-lists deliberately: the reader above is
            /receiving/packing-list, and a history one character away from it is a
            typo that lands on the wrong screen silently. Detail before list in
            source order is not required here (both are `exact`), but they are kept
            adjacent so neither is edited without the other. */}
        <Route exact path="/receiving/shipments" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ShipmentsPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/receiving/shipments/:id" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <ShipmentDetail />
            : <Redirect to="/" />
        )} />

        {/* Phase 1b — Parts Catalog + Inventory (new TSX surfaces alongside
            legacy /parts during the Week 3–4 transition). Order matters:
            /parts-catalog/:id is keyed below the static list.

            The list gates are userCanAccess('catalogRead'): the four receiving
            capabilities PLUS can_financial, matching CATALOG_READ_CAPS in
            parts-catalog-router.js and the inventory list gate. The bookkeeper
            reads both lists to price what the shop receives, and holds nothing
            that can move stock; the write routes behind these pages stay on the
            narrower keys. /parts-catalog/:id deliberately does NOT admit
            financial, because that page also reads sku-history, which the API
            gates to operations and admin. */}
        <Route exact path="/parts-catalog" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing
            || user.can_procurement || user.can_financial
            ? <PartsCatalogPage />
            : <Redirect to="/" />
        )} />
        {/* ERP - Catalog from a document (039). BEFORE /parts-catalog/:id, which
            would otherwise swallow the literal "import" segment and hand it to a
            detail page expecting an integer id. */}
        <Route exact path="/parts-catalog/import" render={() => (
          user.isadmin || user.can_admin || user.can_procurement
            ? <CatalogImportPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/parts-catalog/:id" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement
            ? <PartMPNDetail />
            : <Redirect to="/" />
        )} />
        {/* Product BOMs: every finished product's parts list. Same read gate as the
            catalog list; the writes behind BomPage are gated by the API on
            can_manufacturing. The old /parts/models paths redirect, params carried. */}
        <Redirect exact from="/parts/models" to="/parts/boms" />
        <Redirect from="/parts/models/:code" to="/parts/boms/:code" />
        <Route exact path="/parts/boms" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing
            || user.can_procurement || user.can_financial
            ? <ProductBomsPage />
            : <Redirect to="/" />
        )} />
        {/* Pulls (R&D, repair, scrap): the same gate as the nav's 'pulls' key and the API
            (operations | manufacturing | rnd). */}
        <Route exact path="/parts/pulls" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_rnd
            ? <PullsReportPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/parts/scrap-bin" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing
            ? <ScrapBinPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/parts/boms/:key" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing
            || user.can_procurement || user.can_financial
            ? <BomPage />
            : <Redirect to="/" />
        )} />
        {/* Work orders. The nav's workOrders key (the models' read set plus manager);
            writes are gated by the API (manufacturing, and manager for approve,
            hold, cancel and close). */}
        <Route exact path="/work-orders" render={() => (
          userCanAccess({ require: 'workOrders' }, user)
            ? <WorkOrdersPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/work-orders/:id" render={() => (
          userCanAccess({ require: 'workOrders' }, user)
            ? <WorkOrderPage />
            : <Redirect to="/" />
        )} />
        {/* The one Assembly pool's pick list. Same read gate as work orders; picks are manufacturing. */}
        <Route exact path="/pick-list" render={() => (
          userCanAccess({ require: 'workOrders' }, user)
            ? <AssemblyPickPage />
            : <Redirect to="/" />
        )} />
        <Route exact path="/inventory" render={() => (
          user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing
            || user.can_procurement || user.can_financial
            ? <InventoryPage />
            : <Redirect to="/" />
        )} />
        {/* Receipts — any authenticated user scans/reviews their OWN receipts. */}
        <Route exact path="/receipts" component={ReceiptsPage} />
        {/* Requests: any signed-in employee asks; the server decides who sees what. */}
        <Route exact path="/requisitions" component={RequisitionsPage} />
        <Route exact path="/requisitions/new" component={NewRequisitionPage} />
        <Route exact path="/requisitions/:id" render={(props) => <RequisitionDetailPage key={props.match.params.id} />} />
        <Route exact path="/node-plans" component={NodePlansPage} />
        <Route path="/node-plans/:id" component={NodePlanPage} />
        <Route exact path="/spark" component={SparkPage} />
        <Route exact path="/spark/me" component={SparkMyWork} />
        <Route exact path="/spark/studio" component={SparkStudio} />
        <Route exact path="/spark/projects/:id" render={(props) => <SparkProjectPage key={props.match.params.id} />} />
        <Route exact path="/admin/asana-import" render={() => (user.isadmin || user.can_admin) ? <AsanaImport /> : <Redirect to="/" />} />
        <Route exact path="/reconcile" render={() => user.can_financial ? <ReconcilePage /> : <Redirect to="/receipts" />} />
        <Route exact path="/reconcile/periods/:periodId" render={() => user.can_financial ? <ReconcileBoard /> : <Redirect to="/receipts" />} />
        {/* Accounts payable (ERP chunk 2). Strict financial, same family as Reconcile. */}
        <Route exact path="/invoices" render={() => user.can_financial ? <InvoiceQueuePage /> : <Redirect to="/" />} />
        <Route exact path="/invoices/:invoiceId" render={() => user.can_financial ? <InvoiceDetailPage /> : <Redirect to="/" />} />
        <Route exact path="/receipts/ledger" render={() => user.can_financial ? <LedgerPage /> : <Redirect to="/receipts" />} />
        <Route exact path="/receipts/insights" render={() => user.can_financial ? <InsightsPage /> : <Redirect to="/receipts" />} />

        {/* key on pid so navigating receipt→receipt remounts: fresh form state + timers */}
        <Route exact path="/receipts/:pid" render={(props) => <ReceiptReviewPage key={props.match.params.pid} />} />
        {/* Financial-only — strict gate (admins must hold can_financial too). */}
        <Route exact path="/financial-logs" render={() => (
          user.can_financial ? <FinancialLogsPage /> : <Redirect to="/" />
        )} />
        {/* Family editors, not just admins: the page's admin-only controls hide
            for everyone else, and the API enforces the same split. */}
        <Route exact path="/admin/part-families" render={() => (
          canEditFamilies(user) ? <PartFamiliesAdmin /> : <Redirect to="/" />
        )} />
        <Route exact path="/admin/part-families/:id" render={() => (
          canEditFamilies(user) ? <PartFamilyPage /> : <Redirect to="/" />
        )} />
        {/* Places stock can be. Admin because adding one changes where the whole
            company can receive; the backend POST/PATCH is operations|admin, so
            this route is the tighter of the two on purpose. A new deployment has
            NO locations at all and cannot receive until this page is used. */}
        <Route exact path="/admin/locations" render={() => (
          user.isadmin || user.can_admin ? <LocationsAdmin /> : <Redirect to="/" />
        )} />
        <Route exact path="/admin/cards" render={() => (
          user.isadmin || user.can_admin || user.can_financial ? <CardManager /> : <Redirect to="/" />
        )} />
        {/* ERP - Vendor master (Phase 3, chunk 1). Mirrors the backend gate on
            POST/PATCH /api/procurement/vendors (procurement | admin). */}
        <Route exact path="/admin/vendors" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <VendorsAdmin /> : <Redirect to="/" />
        )} />

        {/* ERP - Approval settings (approval-flow phase). Admin only, NOT
            can_procurement — this configures a money control (who may approve,
            the dollar threshold), not the day-to-day PO workflow. */}
        <Route exact path="/admin/approval-settings" render={() => (
          user.isadmin || user.can_admin ? <ApprovalSettings /> : <Redirect to="/" />
        )} />

        {/* ERP - Purchase orders (Phase 6, chunk 1). Mirrors the backend gate on
            /api/procurement/purchase-orders (procurement | admin). Order matters:
            /procurement/po/:id is a child route sharing the same capability gate. */}
        <Route exact path="/procurement" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <PurchaseOrderList /> : <Redirect to="/" />
        )} />
        {/* ERP - Purchase order create form. Used to be a drawer on the Receiving
            Hub (Receiving/ProcurementOrderForm.tsx); moved here as its own page
            (Mannie, final: "POs on the PO page, Receiving on the Receiving
            page"). Same capability gate as the rest of the PO module. No
            separate navTree/routeInstances entry needed: it sits under the
            /procurement prefix rule, which already covers it (both instances). */}
        <Route exact path="/procurement/new" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <PurchaseOrderForm /> : <Redirect to="/" />
        )} />
        {/* key on :id so navigating PO→PO (Amend pushes to the new draft's own
            URL) remounts: v5 reuses the instance on a param-only change
            otherwise, and Amend is exactly the action that self-navigates
            within this route. Matches the /receipts/:pid precedent above. */}
        <Route exact path="/procurement/po/:id" render={(props) => (
          user.isadmin || user.can_admin || user.can_procurement || user.can_financial
            ? <PurchaseOrderDetail key={props.match.params.id} />
            : <Redirect to="/" />
        )} />

        {/* ERP - PO import review (Phase 9, chunk 1). Same gate as the rest of
            the PO module: procurement | admin, matching
            po-import-router.js's requireCapability. */}
        <Route exact path="/procurement/import" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <PoImportReview /> : <Redirect to="/" />
        )} />

        {/* ERP - Returns (037). Stock we rejected and what is owed back for it.
            Same gate as the rest of the procurement surface, and no separate
            routeInstances entry needed: the /procurement prefix rule covers it
            on both instances. */}
        <Route exact path="/procurement/returns" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <PurchaseReturnsPage /> : <Redirect to="/" />
        )} />

        {/* ERP - Bill matching (chunk 2, phase 6). The buyers' side of billing:
            unbilled order lines beside unmatched invoice lines. Procurement gate,
            matching match-board-router.js — the invoice QUEUE stays financial. */}
        <Route exact path="/procurement/match-board" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <MatchBoardPage /> : <Redirect to="/" />
        )} />

        {/* ERP - Board returns (048). Failed BOARDS going back to their vendor,
            one row per physical unit, with a printed label per board and a
            packing slip for the box.
            A different object from /procurement/returns above, not a view onto
            it: that one is a credit claim against a quantity of a catalog part,
            this one is a shipment of named units, most of which have no purchase
            order behind them at all.
            WIDER GATE than its neighbors, matching requireCapability(
            'procurement', 'manufacturing') on the router: the person holding the
            dead board is a flash-station tech, and a tech who cannot open the
            page cannot record the failure. */}
        <Route exact path="/procurement/board-returns" render={() => (
          user.isadmin || user.can_admin || user.can_procurement || user.can_manufacturing
            ? <BoardReturnsPage /> : <Redirect to="/" />
        )} />
        <Route exact path="/procurement/board-returns/:id" render={() => (
          user.isadmin || user.can_admin || user.can_procurement || user.can_manufacturing
            ? <BoardReturnManifest /> : <Redirect to="/" />
        )} />

        {/* ERP - Scan to stock. Same engine as the import above, opposite
            errand: ONE document off a box that just landed, and the goods go
            to stock on confirm. Same gate, and no separate routeInstances
            entry needed — the /procurement prefix rule covers it on both
            instances, exactly as it covers /procurement/new. */}
        <Route exact path="/procurement/scan" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <PoScanPage /> : <Redirect to="/" />
        )} />

        {/* ERP - Quote to draft PO. A supplier's quotation in, a DRAFT purchase
            order out — the opposite end of the lifecycle from the scan above,
            which ends in stock. Same gate, matching quote-draft-router.js, and
            no separate routeInstances entry needed: the /procurement prefix rule
            covers it on both instances, exactly as it covers /procurement/new. */}
        <Route exact path="/procurement/quote" render={() => (
          user.isadmin || user.can_admin || user.can_procurement ? <QuoteDraftPage /> : <Redirect to="/" />
        )} />

        <Route exact path="/maintenance" component={MaintLogPage} />

        {/* RMA routes */}
        <Route exact path="/rma" component={RMAList} />
        <Route exact path="/rma/catalog" component={RMACatalogAdmin} />
        <Route exact path="/rma/submit" component={RMASubmit} />
        <Route exact path="/rma/status" component={RMAStatus} />
        <Route exact path="/rma/:id" component={RMADetails} />

        <Route exact path="/serialgen" component={SerialNumberGenerator} />
        <Route exact path="/serialmint" component={SerialMint} />
        <Route exact path="/faaregistrations" component={FAARegistrations} />

        <Route exact path="/connorstars" component={GoldStars} />
        <Route exact path="/leaderboards" component={Leaderboard} />
        <Route exact path="/timeline" component={Timeline} />

        <Route exact path="/batch_print" component={BatchPrint} />
        <Route exact path="/label-maker" component={LabelMaker} />

        <Route exact path="/statistics" component={Statistics} />

        {/* ERP - Dashboard */}
        <Route exact path="/dashboard" render={() => <Redirect to="/" />} />

        {/* ERP - Logistics & Packing */}
        <Route exact path="/dispatch" component={DispatchPage} />
        <Route exact path="/packing/:id" component={PackingPage} />
        <Route exact path="/vehicles" component={VehiclesPage} />
        <Route exact path="/tour-timeline" component={SchedulePage} />

        {/* ERP - Events */}
        <Route exact path="/events" component={EventList} />
        <Route exact path="/events/new" component={EventForm} />
        <Route exact path="/calendar" component={EventCalendar} />
        <Route exact path="/events/tours" component={TourList} />
        <Route exact path="/events/tours/:id" component={TourDetail} />
        <Route exact path="/events/monday-inbox" component={MondayInbox} />
        <Route exact path="/events/:id/edit" component={EventForm} />
        <Route exact path="/events/:id" component={EventDetail} />

        {/* ERP - Pilots: POA at /pilots, airspace queue at /pilots/airspace */}
        <Route exact path="/pilots" component={POAPage} />
        <Route exact path="/pilots/airspace" component={PilotsPage} />

        {/* ERP - Schedule */}
        <Route exact path="/schedule" component={StaffSchedulePage} />
        <Route exact path="/roster" component={RosterPage} />
        <Route exact path="/my-schedule" component={MySchedulePage} />

        {/* ERP - Sales */}
        <Route exact path="/orders" component={OrdersPage} />
        <Route exact path="/orders/new" component={SalesOrderForm} />
        <Route exact path="/orders/:id/edit" component={SalesOrderForm} />
        <Route exact path="/orders/:id/fulfillment" component={FulfillmentPage} />
        <Route exact path="/orders/:id" component={SalesOrderDetail} />

        {/* ERP - Customers (sales + superadmin only) */}
        <Route exact path="/customers" render={() => (user.isadmin || user.can_sales) ? <CustomerList /> : <Redirect to="/" />} />
        <Route exact path="/customers/new" render={() => (user.isadmin || user.can_sales) ? <CustomerForm /> : <Redirect to="/" />} />
        <Route exact path="/customers/transfers" render={() => (user.isadmin || user.can_sales) ? <CustomerTransfers /> : <Redirect to="/" />} />
        <Route exact path="/customers/:id" render={() => (user.isadmin || user.can_sales) ? <CustomerDetail /> : <Redirect to="/" />} />

        {/* ERP - Assets (Phase 1a — universal asset spine for non-drone gear).
            Order matters: /assets/new, /assets/stable, /assets/type/:typeKey MUST come before
            /assets/:id so the wildcard doesn't swallow the static sub-paths. */}
        <Route exact path="/assets" component={AssetsPage} />
        <Route exact path="/assets/new" component={NewAssetForm} />
        <Route exact path="/assets/stable" component={AssetsStableList} />
        <Route exact path="/assets/type/:typeKey" component={AssetsTypeList} />
        <Route exact path="/assets/:id" component={AssetDetail} />
        <Route exact path="/admin/asset-types" render={() => (user.isadmin || user.can_admin) ? <AssetTypesAdmin /> : <Redirect to="/" />} />
        {/* Vendor types (063). SAME gate as /admin/vendors, deliberately: the
            person who finds a type missing is the buyer entering the vendor, and
            sending them to find an admin is how a taxonomy stops being
            maintained. Nothing here destroys anything (rename is one row, retire
            is reversible, delete is refused while any vendor carries the type),
            which is what makes it safe to widen. /admin/approval-settings stays
            admin-only; that one decides who may release money. Reached from a
            button on the vendors page, so it carries no nav entry of its own. */}
        <Route exact path="/admin/vendor-types" render={() => (user.isadmin || user.can_admin || user.can_procurement) ? <VendorTypesAdmin /> : <Redirect to="/" />} />
        {/* Item kinds ride the same gate for the same reason: the person who
            finds a kind missing is the buyer cataloging the item, and the SKU
            builder offers to add one in place rather than sending them here.
            Same safety, too: rename is one row, retire is reversible, and delete
            is refused while any catalog row carries the kind. */}
        <Route exact path="/admin/item-kinds" render={() => (user.isadmin || user.can_admin || user.can_procurement) ? <ItemKindsAdmin /> : <Redirect to="/" />} />

        {/* Scanner mode (no exact — allows future sub-routes) */}
        <Route path="/scanner" component={ScannerMode} />

        {/* Flight log analysis workspace */}
        <Route exact path="/analyst" component={AnalysisPage} />

      </Switch>
      </InstanceGate>
    );
  };

  // Day-mode body class — owned at the app root so route transitions between
  // themed surfaces don't flash, and so portal-rendered children (modals, hover
  // cards, popovers) inherit the variant regardless of which surface set it.
  // Themed surfaces only style their own containers; they don't touch document.body.
  const dayMode = useThemeStore((s) => s.dayMode);
  useEffect(() => {
    document.body.classList.toggle('day-mode', dayMode);
    return () => document.body.classList.remove('day-mode');
  }, [dayMode]);

  // Routes that hide the navbar and Flerpie (full-screen displays)
  // pilot-simulator: the easter egg is a full-page takeover — nothing but the
  // game floating in its stars, so the nav and Flerpie stay out. The game
  // carries its own "Back to Firefleet" button.
  const hideNavbarRoutes = ['/office', '/shop', '/scanner', '/analyst', '/pilot-simulator'];

  // --nav-h: how far down the page <main> starts (the nav, or 0 on a full-screen route). The
  // Tailwind h-page / min-h-page utilities read it, so a page fills the window under the nav
  // instead of being a whole window tall and scrolling by the nav's height onto blank space.
  const [mainEl, setMainEl] = useState(null);
  useLayoutEffect(() => {
    if (!mainEl) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--nav-h', `${Math.round(mainEl.getBoundingClientRect().top + window.scrollY)}px`);
    publish();
    window.addEventListener('resize', publish);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    ro?.observe(document.body);
    return () => {
      window.removeEventListener('resize', publish);
      ro?.disconnect();
    };
  }, [mainEl]);

  return (
    <ToastProvider>
      <ConfirmProvider>
        {/* The boundary sits around the router and NOT around the prompt: a tab on
            an old build that hits a missing chunk must keep its Reload button. */}
        <RootErrorBoundary>
          <Router>
            <RecentRoutes />
            <Switch>
              {/* Standalone popup handoff for the Firefly Pilot GCS integration.
                  Self-checking (silent refresh → Login → consent), so it sits
                  above the normal auth gate and renders without app chrome. */}
              <Route path="/pilot-auth" component={PilotAuth} />
              {/* Bot sign-in consent (OAuth). Same standalone shape as /pilot-auth. */}
              <Route path="/oauth/consent" component={OAuthConsent} />
              <Route>
                <div className="App">
                  <NavBarWrapper hideRoutes={hideNavbarRoutes} />
                  <main ref={setMainEl} className={`pl-[var(--flerpie-dock,0px)] transition-[padding-left] ${DOCK_MOTION}`}>{renderRoutes()}</main>
                  <FlerpieWrapper hideRoutes={hideNavbarRoutes} />
                </div>
              </Route>
            </Switch>
          </Router>
        </RootErrorBoundary>
        <UpdatePrompt />
      </ConfirmProvider>
    </ToastProvider>
  );
};

// Send URLs this deployment does not have back to the dashboard.
//
// Nav gating hides the doors; this closes them. A bookmark, a stale link or a
// typed URL would otherwise render a feature the product does not ship — with
// live API calls against a backend that has no such data.
//
// Home, not a 404: these routes are not missing, they are simply not part of
// this product, and a 404 reads as "something is broken".
//
// On Firefleet this is a no-op by construction — see routeInstances.ts.
const InstanceGate = ({ children }) => {
  const location = useLocation();
  if (!routeAllowedOnInstance(location.pathname)) return <Redirect to="/" />;
  return children;
};

// Hide Flerpie on full-screen routes (analyst has its own chat).
const FlerpieWrapper = ({ hideRoutes }) => {
  const location = useLocation();
  const flerpieHidden = useNavPrefsStore((state) => state.flerpieHidden);
  if (isSwarm) return null;       // no Flerpie on Swarm (decision D4)
  if (flerpieHidden) return null; // user opted out via the nav user-menu toggle
  if (hideRoutes.some((route) => location.pathname.startsWith(route))) return null;
  return <FlerpieChatBot />;
};

// Wrapper component to conditionally render NavBar based on route
const NavBarWrapper = ({ hideRoutes }) => {
  const location = useLocation();
  const user = useUserStore((state) => state.user);
  if (!user?.loggedin) return null;
  if (hideRoutes.some((route) => location.pathname.startsWith(route))) return null;
  return <NavBar />;
};

export default App;
